import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';

/**
 * The technician's own absences. Three calls, and no fourth: there is no update — correcting a
 * period is deleting it and declaring it again (D10) — and no backoffice write path, since the
 * technician is the sole author (D4). The backoffice reads them through the record instead.
 */

/**
 * Mirrors `AVAILABILITY_STATES` in `backend/types/technicians.ts` — a value added on one side
 * only would be silently dropped here or 400 there. Exported as the list and not just the union
 * so the filter's options and the URL's validation read the same three words.
 *
 * `upcoming` is a display refinement of available, not a third state: a period declared for next
 * month does not stop a technician being proposed today.
 */
export const AVAILABILITY_STATES = ['available', 'unavailable', 'upcoming'] as const;
export type AvailabilityState = (typeof AVAILABILITY_STATES)[number];

export type PeriodState = 'current' | 'upcoming' | 'past';

/**
 * One declared period. Dates are `YYYY-MM-DD` strings, and `state` and `days` are computed by
 * Postgres — the front does no date arithmetic, which is what keeps the single point of decision
 * single.
 */
export interface UnavailabilityPeriod {
  id: number;
  startDate: string;
  endDate: string;
  reason: string | null;
  days: number;
  state: PeriodState;
}

export interface MyUnavailabilities {
  availability: AvailabilityState;
  periods: UnavailabilityPeriod[];
}

/** What the sheet posts. `YYYY-MM-DD`, the shape `<input type="date">` already produces. */
export interface DeclareUnavailabilityInput {
  startDate: string;
  endDate: string;
  reason?: string | null;
}

const BASE = 'technicians/me/unavailabilities';
const jsonHeaders = { 'Content-Type': 'application/json' };

export const fetchMyUnavailabilities = async (): Promise<MyUnavailabilities> =>
  unwrap<MyUnavailabilities>(await fetchWithAuth(BASE), 'Failed to load your unavailability periods');

export const declareUnavailability = async (
  input: DeclareUnavailabilityInput
): Promise<UnavailabilityPeriod> =>
  unwrap<UnavailabilityPeriod>(
    await fetchWithAuth(BASE, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(input) }),
    'Failed to declare the unavailability period'
  );

export const deleteUnavailability = async (id: number): Promise<void> => {
  await unwrap<{ id: number }>(
    await fetchWithAuth(`${BASE}/${id}`, { method: 'DELETE' }),
    'Failed to delete the unavailability period'
  );
};
